import { useQuery } from '@apollo/client';
import { IconTextButtonBlock } from 'components/minute/view/IconTextButtonBlock';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { MapList } from 'components/site/pages/LandingPageContent/MapList';
import { motion } from 'framer-motion';
import { CurrentUserWorldMaps } from 'graphql/query/__generated__/CurrentUserWorldMaps';
import { WORLD_MAP_CURRENT_USER_QUERY } from 'graphql/query/worldQuery';
import { ANIMATION_APPEAR } from 'libs/styles/animations';
import { EBorders } from 'libs/styles/styling';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { CtxModal } from 'session/ctxModal';
import { GlobalContext } from 'session/state/context';
import { SUBSCRIPTION_CONFIG } from 'static/config/subscriptionConfig';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const LandingPageContent = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const {
        state: { loggedUser }
    } = useContext(GlobalContext);

    const navigate = useNavigate();

    const query = useQuery<CurrentUserWorldMaps>(WORLD_MAP_CURRENT_USER_QUERY);

    function openModal() {
        ctxModal.setModal({
            type: EModal.UPGRADE
        });
    }

    function openLiveChat() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).HubSpotConversations?.widget?.open?.();
    }

    const userMaps = query.data?.currentUserWorldMaps ?? [];
    const maxMaps = SUBSCRIPTION_CONFIG[loggedUser.subscriptionTier].maxMaps;
    const reachedMapLimit = maxMaps ? userMaps.length >= maxMaps : false;

    return (
        <S_LandingPageContent className={props.className}>
            <S_Left>
                <MapList />
            </S_Left>
            <S_Right>
                <motion.div {...ANIMATION_APPEAR}>
                    <S_IconTextButtonBlock
                        iconName="map-location-dot"
                        title={Localization.DO_YOU_WANT_NEW_MAP}
                        subtitle={`${userMaps.length}/${maxMaps ?? '∞'}`}
                        buttonText={
                            reachedMapLimit
                                ? Localization.UPGRADE_ACTION
                                : Localization.CREATE_ACTION
                        }
                        buttonAction={() => {
                            if (reachedMapLimit) {
                                openModal();
                            } else {
                                navigate('/create');
                            }
                        }}
                    />
                </motion.div>
                {SUBSCRIPTION_CONFIG[loggedUser?.subscriptionTier]?.name !== 'Full' && (
                    <motion.div {...ANIMATION_APPEAR}>
                        <S_IconTextButtonBlock
                            iconName="rocket-launch"
                            title={Localization.UPGRADE_EXPERIENCE}
                            subtitle={Localization.UPGRADE_EXPERIENCE_DESC}
                            buttonText={Localization.UPGRADE_ACTION}
                            buttonAction={openModal}
                        />
                    </motion.div>
                )}
                <motion.div {...ANIMATION_APPEAR}>
                    <S_IconTextButtonBlock
                        iconName="envelope"
                        title={Localization.NEED_HELP}
                        subtitle={Localization.LET_US_KNOW}
                        buttonText={Localization.CONTACT_ACTION}
                        buttonAction={openLiveChat}
                    />
                </motion.div>
            </S_Right>
        </S_LandingPageContent>
    );
};

const S_LandingPageContent = styled.div`
    display: flex;
    width: 830px;
`;

const S_Left = styled.div`
    width: 550px;
    margin-right: 30px;
    display: flex;
    flex-direction: column;
`;

const S_Right = styled.div`
    width: 250px;
    display: flex;
    flex-direction: column;
`;

const S_IconTextButtonBlock = styled(IconTextButtonBlock)`
    margin: 0 0 30px 0;
    width: 250px;
    background-color: #efefef;
    border-radius: 7px;
    box-shadow: 5px 5px 10px 0px rgba(0, 0, 0, 0.1);
    border: 1px solid ${EBorders.DARK};
`;
